
import { useState } from "react";
import { LogOut, Menu, FileText, Briefcase, History, ChevronRight, Home, Phone, Navigation, Newspaper, Users, Smartphone } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { BlogAdminManager } from "./BlogAdminManager";
import { AdminSidebar } from "./AdminSidebar";
import { ServicesManager } from "./ServicesManager";
import { NossaHistoriaManager } from "./NossaHistoriaManager";
import { ImprensaManager } from "./ImprensaManager";
import { UsuariosManager } from "./UsuariosManager";
import { HomeManager } from "./HomeManager";
import { ContatoManager } from "./ContatoManager";
import { TutorialManager } from "./TutorialManager";
import { NavManager } from "./NavManager";
import { MobileHomeManager } from "./MobileHomeManager";
import { CalendarioManager } from "./CalendarioManager";
import { AvaliacoesManager } from "./AvaliacoesManager";
import { ConsultoriosManager } from "./ConsultoriosManager";
import { LogoManager } from "./LogoManager";

const pageTitles: Record<string, string> = {
  blog: "Blog",
  "blog-page": "Blog",
  tutorial:   "Tutorial do Painel",
  servicos: "Serviços",
  usuarios: "Usuários & Permissões",
  imprensa: "Imprensa",
  calendario: "Calendário",
  historia: "Quem Somos",
  inicio:    "Início",
  contato:   "Contato",
  navegacao: "Cabeçalho & Rodapé",
};

const pageSections: Record<string, string> = {
  blog: "Conteúdo",
  "blog-page": "Conteúdo",
  tutorial: "Ajuda",
  categories: "Conteúdo",
  imprensa: "Conteúdo",
  inicio:    "Site",
  contato:   "Site",
  navegacao: "Site",
  servicos: "Site",
  depoimentos: "Site",
  jornada: "Site",
  historia: "Site",
  faq: "Site",
  usuarios: "Gestão",
  calendario: "Gestão",
};

// ─── Quick-action cards shown on the home screen ───────────────────────────
const quickActions = [
  // Site
  { id: "inicio",      title: "Início",              desc: "Banner e seções da home",        icon: Home,         color: "bg-green-50 text-green-700" },
  { id: "blog",        title: "Blog",                desc: "Criar e editar posts",            icon: FileText,     color: "bg-emerald-50 text-emerald-700" },
  { id: "servicos",    title: "Serviços",            desc: "Gerenciar tipos de atendimento",  icon: Briefcase,    color: "bg-sky-50 text-sky-700" },
  { id: "imprensa",    title: "Imprensa",            desc: "Aparições na mídia",              icon: Newspaper,    color: "bg-orange-50 text-orange-700" },
  { id: "contato",     title: "Contato",             desc: "Informações de contato",          icon: Phone,        color: "bg-cyan-50 text-cyan-700" },
  // Conteúdo
  { id: "historia",    title: "Quem Somos",          desc: "Equipe e trajetória",             icon: History,      color: "bg-teal-50 text-teal-700" },
  // Gestão
  { id: "usuarios",    title: "Usuários",            desc: "Permissões e histórico",          icon: Users,        color: "bg-indigo-50 text-indigo-700" },
  { id: "navegacao",   title: "Cabeçalho & Rodapé",  desc: "Ordenar itens de navegação",     icon: Navigation,   color: "bg-purple-50 text-purple-700" },
  { id: "mobile",        title: "Página Mobile",      desc: "Links e perfil no celular",       icon: Smartphone,   color: "bg-fuchsia-50 text-fuchsia-700" },
];

// ─── Page wrapper ──────────────────────────────────────────────────────────
function PageShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-bold text-gray-800 font-primary">{title}</h2>
        <div className="h-0.5 w-8 bg-brand-primary rounded-full mt-1.5" />
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ─── "Em desenvolvimento" placeholder ────────────────────────────────────
function ComingSoon({ title }: { title: string }) {
  return (
    <PageShell title={title}>
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
          <span className="text-2xl">🔧</span>
        </div>
        <p className="text-gray-500 text-sm font-medium">Em desenvolvimento</p>
        <p className="text-gray-400 text-xs mt-1">Esta funcionalidade estará disponível em breve.</p>
      </div>
    </PageShell>
  );
}

// ─── Home screen ──────────────────────────────────────────────────────────
function HomeScreen({ onNavigate, displayName }: { onNavigate: (id: string) => void; displayName: string }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";

  return (
    <div className="space-y-8">
      {/* Welcome banner */}
      <div
        className="rounded-2xl p-7 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #1F5938 0%, #2d7a50 100%)" }}
      >
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
        <div className="absolute -bottom-10 -right-4 w-56 h-56 rounded-full bg-white/5" />
        <div className="relative z-10">
          <p className="text-white/60 text-sm mb-1">{greeting},</p>
          <h1 className="text-white text-2xl font-bold font-primary mb-1">{displayName}</h1>
          <p className="text-white/50 text-sm">
            {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <span className="absolute bottom-4 right-6 text-4xl select-none opacity-30">⚜️</span>
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-4">Acesso rápido</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => onNavigate(action.id)}
              className="group flex items-center gap-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-brand-primary/20 transition-all duration-200 p-4 text-left"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${action.color}`}>
                <action.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 group-hover:text-brand-primary transition-colors">{action.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">{action.desc}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-brand-primary group-hover:translate-x-0.5 transition-all shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export function AdminDashboard() {
  const { signOut, profile } = useAuth();
  const [activeTab, setActiveTab] = useState("home");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => { await signOut(); };

  const displayName = profile?.name || profile?.email || "Administrador";
  const initials = displayName
    .split(" ")
    .slice(0, 2)
    .map((n: string) => n[0])
    .join("")
    .toUpperCase();

  const renderContent = () => {
    switch (activeTab) {
      case "home":        return <HomeScreen onNavigate={setActiveTab} displayName={displayName} />;
      case "blog":        return <PageShell title="Blog"><BlogAdminManager /></PageShell>;
      case "servicos":    return <PageShell title="Serviços"><ServicesManager /></PageShell>;
      case "historia":    return <PageShell title="Quem Somos"><NossaHistoriaManager /></PageShell>;
      case "usuarios":    return <PageShell title="Usuários & Permissões"><UsuariosManager /></PageShell>;
      case "imprensa":    return <PageShell title="Imprensa"><ImprensaManager /></PageShell>;
      case "inicio":      return <PageShell title="Início"><HomeManager /></PageShell>;
      case "contato":     return <PageShell title="Contato"><ContatoManager /></PageShell>;
      case "navegacao":      return <PageShell title="Cabeçalho & Rodapé"><NavManager /></PageShell>;
      case "mobile":         return <PageShell title="Página Mobile"><MobileHomeManager /></PageShell>;
      case "blog-page":   return <PageShell title="Blog"><BlogAdminManager /></PageShell>;
      case "tutorial":     return <PageShell title="Tutorial do Painel"><TutorialManager /></PageShell>;
      case "calendario":   return <PageShell title="Calendário"><CalendarioManager /></PageShell>;
      case "avaliacoes":   return <PageShell title="Avaliações"><AvaliacoesManager /></PageShell>;
      case "consultorios": return <PageShell title="Consultórios"><ConsultoriosManager /></PageShell>;
      case "logo":         return <PageShell title="Logo do Site"><LogoManager /></PageShell>;
      default:             return <HomeScreen onNavigate={setActiveTab} displayName={displayName} />;
    }
  };

  const currentTitle = pageTitles[activeTab];
  const currentSection = pageSections[activeTab];

  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      <AdminSidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`flex flex-col min-h-screen transition-[padding] duration-300 ease-in-out ${sidebarOpen ? "lg:pl-64" : ""}`}>

        {/* ── Topbar ─────────────────────────────────────────────────────── */}
        <header className="bg-white border-b border-gray-100 px-4 sm:px-6 h-14 flex items-center justify-between sticky top-0 z-30">

          {/* Left */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="w-8 h-8 rounded-lg bg-brand-primary/8 hover:bg-brand-primary/15 text-brand-primary flex items-center justify-center transition-colors"
            >
              <Menu className="w-4 h-4" />
            </button>

            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-sm">
              <button
                onClick={() => setActiveTab("home")}
                className="text-gray-400 hover:text-brand-primary transition-colors font-medium"
              >
                Painel
              </button>
              {currentSection && (
                <>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                  <span className="text-gray-400">{currentSection}</span>
                </>
              )}
              {currentTitle && (
                <>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                  <span className="text-gray-700 font-semibold">{currentTitle}</span>
                </>
              )}
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">
            {/* User pill */}
            <div className="hidden sm:flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-1.5">
              <div className="w-6 h-6 rounded-lg bg-brand-primary flex items-center justify-center shrink-0">
                <span className="text-white text-[10px] font-bold leading-none">{initials}</span>
              </div>
              <span className="text-xs text-gray-600 font-medium max-w-[110px] truncate">{displayName}</span>
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              title="Sair"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* ── Page content ───────────────────────────────────────────────── */}
        <main className="flex-1 p-4 sm:p-6 max-w-6xl w-full mx-auto">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}
